import { useCallback, useEffect, useMemo, useState } from 'react'
import { getDashboardWidget } from '../../../api/insights'
import { ApiError } from '../../../api/problem'
import type { DashboardFilter, WidgetKey, WidgetStatus } from '../../../types/insights'

export interface UseWidgetDataResult<T> {
  status: WidgetStatus | 'loading'
  data: T | null
  message: string | null
  retry: () => void
}

/**
 * Each widget fetches independently (AC26) — this hook owns the per-widget
 * fetch/abort/error lifecycle so a `DashboardScreen` never has one shared
 * loading/error state that could blank the whole page.
 *
 * `skipRefetch` implements the AC10 "only sibling widgets re-fetch" rule:
 * the widget that *originated* a cross-filter click already reflects the
 * segment the user selected, so `DashboardScreen` passes `skipRefetch=true`
 * for that one widget on the render where the filter changed because of its
 * own click — every other widget (and a manual retry) still fetches.
 */
export function useWidgetData<T>(
  widgetKey: WidgetKey,
  filter: DashboardFilter,
  skipRefetch: boolean,
): UseWidgetDataResult<T> {
  const [status, setStatus] = useState<WidgetStatus | 'loading'>('loading')
  const [data, setData] = useState<T | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const filterKey = useMemo(() => JSON.stringify(filter), [filter])

  const fetchNow = useCallback(() => {
    const controller = new AbortController()
    setStatus('loading')
    getDashboardWidget<T>(widgetKey, filter, controller.signal)
      .then((result) => {
        setStatus(result.status)
        setData(result.data)
        setMessage(result.message ?? null)
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        setStatus('error')
        setData(null)
        setMessage(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load this tile. The rest of the dashboard is unaffected.')
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetKey, filterKey])

  useEffect(() => {
    if (skipRefetch) return
    return fetchNow()
  }, [filterKey, skipRefetch, fetchNow])

  return { status, data, message, retry: fetchNow }
}
